import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiCallWithRetry, client } from '../../client';

// Fetch single order
export const fetchOrder = createAsyncThunk(
  'order/fetchOrder',
  async ({ reorderUuid, accountUuid }, { rejectWithValue }) => {
    const apiCall = async () => {
      return await client.get(`/accounts/${accountUuid}/orders/${reorderUuid}`);
    };

    try {
      const response = await apiCallWithRetry(apiCall, 3, 1000);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update existing order
export const updateOrder = createAsyncThunk(
  'order/updateOrder',
  async ({ accountUuid, orderUuid, formValues, originalOrder, userUuid, metadata }, { rejectWithValue }) => {
    const { compare } = await import('fast-json-patch');
    const { default: isEqual } = await import('lodash/isEqual');

    const {
      slitLowDose,
      slitLowDoseQuantity,
      slitMaintenance,
      slitMaintenanceQuantity,
      ...values
    } = formValues;

    const formattedValues = {
      ...values,
      slitLowDoseQuantity:
        slitLowDose && slitLowDoseQuantity ? slitLowDoseQuantity : 0,
      slitMaintenanceQuantity:
        slitMaintenance && slitMaintenanceQuantity
          ? slitMaintenanceQuantity
          : 0,
    };

    const orderCopy = { ...originalOrder.data };
    delete formattedValues.scitAllergens; // will be patched manually
    delete formattedValues.slitLowDose; // internal usage
    delete formattedValues.slitMaintenance; // internal usage
    delete orderCopy.scitAllergens;
    delete orderCopy.slitLowDose;
    delete orderCopy.slitMaintenance;

    // compare all the values except deleted ones
    const patches = compare(orderCopy, formattedValues);
    // create a patch for an array manually
    if (!isEqual(originalOrder.data.scitAllergens, formValues.scitAllergens)) {
      patches.push({
        op: 'replace',
        path: '/scitAllergens',
        value: formValues.scitAllergens,
      });
    }

    if (patches.length === 0) {
      // No changes to save
      return {
        data: originalOrder.data,
        originalOrder,
        patientName: metadata?.patientInfo?.name || '',
        noChanges: true,
      };
    }

    const apiCall = async () => {
      return await client.patch(`/accounts/${accountUuid}/orders/${orderUuid}`, patches);
    };

    try {
      const response = await apiCallWithRetry(apiCall, 3, 1000);
      if (response.ok) {
        return {
          data: response.data,
          originalOrder,
          patientName: metadata?.patientInfo?.name || '',
          originalFormValues: { slitLowDose, slitMaintenance },
        };
      } else {
        return rejectWithValue({
          status: response?.status,
          errors: response?.data?.validationErrors || {},
          correlationUUID: response?.data?.correlationUUID,
          userUUID: userUuid,
          resetSlitFields: { slitLowDose, slitMaintenance },
          ...response?.data,
        });
      }
    } catch (error) {
      return rejectWithValue({
        status: error.response?.status,
        errors: error.response?.data?.validationErrors || {},
        correlationUUID: error.response?.data?.correlationUUID,
        userUUID: userUuid,
        resetSlitFields: { slitLowDose, slitMaintenance },
        ...error.response?.data,
      });
    }
  }
);


// Fetch create order metadata (patient info, providers, addresses)
export const fetchCreateOrderMetadata = createAsyncThunk(
  'order/fetchCreateOrderMetadata',
  async ({ accountUuid, patientUuid }, { rejectWithValue }) => {
    const apiCall = async () => {
      return await client.get(`/accounts/${accountUuid}/patients/${patientUuid}/create-order-metadata`);
    };

    try {
      const response = await apiCallWithRetry(apiCall, 3, 1000);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Check immunotherapy report permission
export const checkImmunotherapyPermission = createAsyncThunk(
  'order/checkImmunotherapyPermission',
  async (accountUuid, { rejectWithValue }) => {
    const apiCall = async () => {
      return await client.get(`/accounts/${accountUuid}/account-permissions`);
    };

    try {
      const response = await apiCallWithRetry(apiCall, 3, 1000);
      return response.data?.data?.enabletImmunotherapyReport || false;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Submit order
export const submitOrder = createAsyncThunk(
  'order/submitOrder',
  async ({ formValues, accountUuid, patientUuid }, { rejectWithValue }) => {
    const {
      slitLowDose,
      slitLowDoseQuantity,
      slitMaintenance,
      slitMaintenanceQuantity,
      ...values
    } = formValues;

    const formattedValues = {
      ...values,
      slitLowDoseQuantity:
        slitLowDose && slitLowDoseQuantity ? slitLowDoseQuantity : 0,
      slitMaintenanceQuantity:
        slitMaintenance && slitMaintenanceQuantity
          ? slitMaintenanceQuantity
          : 0,
    };

    const apiCall = async () => {
      return await client.put(`/accounts/${accountUuid}/patients/${patientUuid}/orders`, formattedValues);
    };

    try {
      const response = await apiCallWithRetry(apiCall, 3, 1000);
      if (response.ok) {
        return response.data;
      } else {
        return rejectWithValue({
          status: response?.status,
          errors: response?.data?.validationErrors || {},
          correlationUUID: response?.data?.correlationUUID,
          originalFormValues: { slitLowDose, slitMaintenance },
          ...response?.data,
        });
      }
    } catch (error) {
      return rejectWithValue({
        status: error.response?.status,
        errors: error.response?.data?.validationErrors || {},
        correlationUUID: error.response?.data?.correlationUUID,
        originalFormValues: { slitLowDose, slitMaintenance },
        ...error.response?.data,
      });
    }
  }
);

// Validate order (without saving)
export const validateOrder = createAsyncThunk(
  'order/validateOrder',
  async (orderData, { rejectWithValue }) => {
    const apiCall = async () => {
      return await client.post('/orders?validate-only=true', orderData);
    };

    try {
      const response = await apiCallWithRetry(apiCall, 3, 1000);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);
